import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeeColumnFilterValue } from "@/pages/employees/index/EmployeeColumnFilterValue.tsx";
import { EmployeeColumnFilterValueProps } from "@/pages/employees/index/employee-column-filter-renderers.ts";
import { useJobProfiles } from "@/queries/use-job-profiles.query.ts";
import { useJobRolesSearchInline } from "@/queries/use-job-roles.query.ts";
import { ApiEmployeeColumnOptionId } from "@/types/employees/employee-column.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { Multiselect } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

const toOptionIds = (values: (ApiEmployeeColumnOptionId | null | undefined)[]) =>
  values.filter((item): item is ApiEmployeeColumnOptionId => item !== null && item !== undefined);

function useCanViewPositionSettings() {
  const { canIView } = usePermissions();
  return canIView(ApiPermission.settings_positions_levels);
}

function JobProfileFilterValue({ column, filter, onChange }: EmployeeColumnFilterValueProps) {
  const { t } = useTranslation();
  const jobProfiles = useJobProfiles();

  return (
    <Multiselect
      label={column.title}
      options={jobProfiles.options}
      values={filter.option_ids ?? []}
      onChange={(value) => onChange({ option_ids: toOptionIds(value) })}
      searchable
      isClearable
      showCaption
      captionPerItem
      isLoading={jobProfiles.isLoading}
      emptyTitle={t("common:select")}
    />
  );
}

function JobRoleFilterValue({ column, filter, onChange }: EmployeeColumnFilterValueProps) {
  const { t } = useTranslation();
  const selectedIds = (filter.option_ids ?? []).filter(
    (id): id is number => typeof id === "number"
  );
  const jobRoles = useJobRolesSearchInline(selectedIds);

  return (
    <Multiselect
      label={column.title}
      options={jobRoles.options}
      searchOptions={jobRoles.options}
      onSearchOptions={jobRoles.search}
      loadOptionsOnOpen
      values={filter.option_ids ?? []}
      onChange={(value) => onChange({ option_ids: toOptionIds(value) })}
      searchable
      isClearable
      isLoading={jobRoles.isLoading}
      emptyTitle={t("common:select")}
    />
  );
}

export function EmployeeJobProfileFilterValue(props: EmployeeColumnFilterValueProps) {
  const canView = useCanViewPositionSettings();
  return canView ? <JobProfileFilterValue {...props} /> : <EmployeeColumnFilterValue {...props} />;
}

export function EmployeeJobRoleFilterValue(props: EmployeeColumnFilterValueProps) {
  const canView = useCanViewPositionSettings();
  return canView ? <JobRoleFilterValue {...props} /> : <EmployeeColumnFilterValue {...props} />;
}
